import React, { createContext, useContext, useMemo, useState } from "react";
import type { ClientDetail } from "@app/portal-sdk";

/**
 * Lets the AppShell (which renders above the router outlet) know which client
 * portal, if any, is currently open. ClientPortalLayout sets it on mount and
 * clears it on unmount.
 */
export type ShellContextValue = {
  client: ClientDetail | null;
  canManage: boolean;
  setClient: (client: ClientDetail | null, canManage?: boolean) => void;
  /** MNT-2: the client the admin console has open, so the Clients section can name it beneath All clients. */
  adminClient: { id: string; name: string } | null;
  setAdminClient: (client: { id: string; name: string } | null) => void;
};

const ShellContext = createContext<ShellContextValue | undefined>(undefined);

export const ShellProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [state, setState] = useState<{ client: ClientDetail | null; canManage: boolean }>({
    client: null,
    canManage: false
  });
  const [adminClient, setAdminClient] = useState<{ id: string; name: string } | null>(null);

  const value = useMemo<ShellContextValue>(
    () => ({
      client: state.client,
      canManage: state.canManage,
      setClient: (client, canManage = false) => setState({ client, canManage }),
      adminClient,
      setAdminClient
    }),
    [state, adminClient]
  );

  return <ShellContext.Provider value={value}>{children}</ShellContext.Provider>;
};

export const useShell = (): ShellContextValue => {
  const ctx = useContext(ShellContext);
  if (!ctx) {
    throw new Error("useShell must be used within ShellProvider");
  }
  return ctx;
};
